import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { comoFechaHora } from "@/lib/fechas"

import { Documento } from "./Documento"
import { PortadaDocumento } from "./PortadaDocumento"
import type { VersionDetalle, VersionDoc } from "./tipos"

/**
 * Las versiones congeladas de un documento.
 *
 * ⚠ **Una versión es un snapshot, no una referencia.** Guarda la portada Y el cuerpo
 * enteros: editar el documento después no cambia lo que decía la versión que alguien
 * aprobó. Sin eso, un documento «controlado» no controla nada — es la misma razón por
 * la que una tasa de cambio no se corrige y un contrato emitido congela su texto.
 *
 * ⚠ **El contenido de una versión se pide al abrirla, no con la lista.** Cada snapshot
 * pesa lo que el documento entero, y la lista enseña diez.
 */

export function VersionesDocumento({
  documentoId,
  version,
  puedePublicar,
  onCerrar,
}: {
  documentoId: number
  version: string
  puedePublicar: boolean
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [abierta, setAbierta] = useState<number | null>(null)
  const [notas, setNotas] = useState("")

  const versiones = useQuery({
    queryKey: ["biblioteca", "versiones", documentoId],
    queryFn: () => api.get<VersionDoc[]>(`/api/v1/biblioteca/documentos/${documentoId}/versiones`),
  })

  const detalle = useQuery({
    queryKey: ["biblioteca", "version", abierta],
    queryFn: () =>
      api.get<VersionDetalle>(`/api/v1/biblioteca/documentos/${documentoId}/versiones/${abierta}`),
    enabled: abierta !== null,
  })

  const publicar = useMutation({
    mutationFn: () =>
      api.post<VersionDoc>(`/api/v1/biblioteca/documentos/${documentoId}/versiones`, {
        notas: notas || null,
      }),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["biblioteca", "versiones", documentoId] })
      clienteQuery.invalidateQueries({ queryKey: ["biblioteca", "documento", String(documentoId)] })
      clienteQuery.invalidateQueries({ queryKey: ["biblioteca", "documentos"] })
      setNotas("")
    },
  })

  const snapshot = detalle.data?.snapshot

  return (
    <Modal
      abierto
      titulo="Versiones publicadas"
      descripcion="Cada una congela la portada y el texto tal como estaban al publicarla."
      onCerrar={onCerrar}
      ancho="lg"
    >
      {puedePublicar && (
        <div className="mb-5 superficie p-3">
          <label htmlFor="notas-version" className="mb-1 block text-xs font-medium uppercase">
            Publicar la v{version} de hoy
          </label>
          <div className="flex flex-wrap gap-2">
            <input
              id="notas-version"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Qué cambió en esta edición"
              maxLength={255}
              className="h-9 min-w-[220px] flex-1 rounded-[var(--radius-control)] border px-2 text-sm"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--surface)",
                color: "var(--fg)",
              }}
            />
            <Boton
              variante="primario"
              onClick={() => publicar.mutate()}
              disabled={publicar.isPending}
            >
              {publicar.isPending ? "Publicando…" : "Publicar"}
            </Boton>
          </div>
          {publicar.isError && (
            <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
              {publicar.error instanceof ErrorApi ? publicar.error.message : "No se pudo publicar"}
            </p>
          )}
        </div>
      )}

      {versiones.data?.length === 0 && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Todavía no se ha publicado ninguna versión.
        </p>
      )}

      <ul className="space-y-2">
        {versiones.data?.map((v) => (
          <li key={v.id}>
            <button
              type="button"
              onClick={() => setAbierta(abierta === v.id ? null : v.id)}
              aria-expanded={abierta === v.id}
              className="w-full rounded-[var(--radius-control)] border px-3 py-2 text-left text-sm toque-cristal"
              style={{ borderColor: "var(--border)" }}
            >
              <span className="font-semibold tabular">v{v.version}</span>{" "}
              <span className="tabular" style={{ color: "var(--fg-muted)" }}>
                · {comoFechaHora(v.creado_en)}
              </span>
              {v.notas && <span className="block text-xs">{v.notas}</span>}
            </button>
          </li>
        ))}
      </ul>

      {abierta !== null && (
        <div className="mt-5 border-t pt-4" style={{ borderColor: "var(--border)" }}>
          {detalle.isPending && (
            <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
              Cargando la versión…
            </p>
          )}
          {snapshot && (
            <>
              <PortadaDocumento
                doc={{
                  codigo: String(snapshot.codigo ?? ""),
                  titulo: String(snapshot.titulo ?? ""),
                  subtitulo: (snapshot.subtitulo as string | null) ?? null,
                  clasificacion: (snapshot.clasificacion as string | null) ?? null,
                  version: String(snapshot.version ?? ""),
                  estado: String(snapshot.estado ?? ""),
                  fecha_emision: (snapshot.fecha_emision as string | null) ?? null,
                  proxima_revision: (snapshot.proxima_revision as string | null) ?? null,
                  elaborado_por: (snapshot.elaborado_por as string | null) ?? null,
                  revisado_por: (snapshot.revisado_por as string | null) ?? null,
                  aprobado_por: (snapshot.aprobado_por as string | null) ?? null,
                  relacionados: (snapshot.relacionados as string[] | undefined) ?? [],
                }}
              />
              <Documento cuerpo={snapshot.cuerpo} />
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
